import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Input, ToastContainer } from '../components/UI';
import { useToast } from '../hooks/useToast';

import { API, fileHref } from '../config';

// ── Known-fields cache (cross-form auto-fill) ─────────────────
// Persisted in localStorage under 'soch_kf'. Forms read on init
// and write live as user types, so later forms auto-populate.
const KF_KEY = 'soch_kf';
function loadKF() { try { return JSON.parse(localStorage.getItem(KF_KEY) || '{}'); } catch { return {}; } }
function saveKF(updates) {
  const next = { ...loadKF(), ...updates };
  // Drop empty strings so they don't overwrite good data later
  Object.keys(next).forEach(k => { if (next[k] === '' || next[k] == null) delete next[k]; });
  localStorage.setItem(KF_KEY, JSON.stringify(next));
}
function getKF(key, fallback = '') { return loadKF()[key] || fallback; }

export default function OnboardingPortal() {
  const { toasts, toast } = useToast();
  // Persist key across refreshes
  const [step, setStep] = useState(() => localStorage.getItem('soch_ob_step') || 'key');
  const [key, setKey]   = useState(() => localStorage.getItem('soch_ob_key')  || '');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  // Persist form values across refreshes
  const [formValues, setFormValues] = useState(() => {
    try { return JSON.parse(localStorage.getItem('soch_ob_form') || '{}'); } catch { return {}; }
  });
  const [fileUploads, setFileUploads] = useState({});
  const [uploadingField, setUploadingField] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [acknowledgedDocs, setAcknowledgedDocs] = useState([]);
  const [activeDoc, setActiveDoc] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [taskUploading, setTaskUploading] = useState(null);

  const verifyKey = async (keyToUse) => {
    const k = (keyToUse || key).trim().toUpperCase();
    if (!k) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/onboarding/employee/${k}`);
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setData(d);
      // Pre-fill existing data (server overrides local on fresh load)
      if (d.applicant.onboardingData && Object.keys(d.applicant.onboardingData).length > 0) {
        setFormValues(d.applicant.onboardingData);
        localStorage.setItem('soch_ob_form', JSON.stringify(d.applicant.onboardingData));
      }
      if (d.applicant.acknowledgedDocs) setAcknowledgedDocs(d.applicant.acknowledgedDocs.map(a => a.docId));
      const nextStep = d.applicant.onboardingStatus === 'completed' ? 'done' : 'form';
      setStep(nextStep);
      localStorage.setItem('soch_ob_step', nextStep);
      localStorage.setItem('soch_ob_key', k);
      // Load assignments
      loadTasks(k);
    } catch (err) {
      toast.error(err.message || 'Invalid key. Please check your email.');
    } finally { setLoading(false); }
  };

  // Auto-restore session on mount + read key from URL hash (?key=SOCH-XXX)
  React.useEffect(() => {
    // Check URL for pre-filled key (from email link)
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#[^?]*\?/, ''));
    const urlKey = urlParams.get('key') || hashParams.get('key');
    if (urlKey) {
      const k = urlKey.trim().toUpperCase();
      setKey(k);
      verifyKey(k);
      return;
    }
    // Restore saved session
    const savedKey  = localStorage.getItem('soch_ob_key');
    const savedStep = localStorage.getItem('soch_ob_step');
    if (savedKey && savedStep && savedStep !== 'key') {
      verifyKey(savedKey);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTasks = async (k) => {
    try {
      const res = await fetch(`${API}/assignments/my-tasks/${(k||key).trim().toUpperCase()}`);
      if (res.ok) setTasks(await res.json());
    } catch {}
  };

  const set = (fieldId, val) => setFormValues(p => {
    const updated = { ...p, [fieldId]: val };
    localStorage.setItem('soch_ob_form', JSON.stringify(updated));
    return updated;
  });

  const uploadFile = async (fieldId, file) => {
    setUploadingField(fieldId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('fieldId', fieldId);
      const res = await fetch(`${API}/onboarding/employee/${key.trim().toUpperCase()}/upload`, {
        method: 'POST', body: fd
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setFileUploads(p => ({ ...p, [fieldId]: { fileUrl: d.fileUrl, fileName: d.fileName } }));
      toast.success(`${file.name} uploaded `);
    } catch (err) { toast.error(err.message || 'Upload failed'); }
    finally { setUploadingField(null); }
  };

  const handleSubmit = async () => {
    // Validate required fields
    const tpl = data?.template;
    if (tpl) {
      for (const field of tpl.fields || []) {
        if (!field.required) continue;
        if (['image', 'document', 'file'].includes(field.type)) {
          if (!fileUploads[field.id]) { toast.error(`Please upload: ${field.label}`); return; }
        } else {
          if (!formValues[field.id]) { toast.error(`Please fill in: ${field.label}`); return; }
        }
      }
      // Check all required docs acknowledged
      const requiredDocIds = (tpl.requiredDocs || []).filter(d => d.required).map(d => d.id);
      const missing = requiredDocIds.find(id => !acknowledgedDocs.includes(id));
      if (missing) {
        const doc = tpl.requiredDocs.find(d => d.id === missing);
        toast.error(`Please read and acknowledge: ${doc?.title}`); return;
      }
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/onboarding/employee/${key.trim().toUpperCase()}/submit`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formValues)
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setStep('done');
      localStorage.setItem('soch_ob_step', 'done');
    } catch (err) { toast.error(err.message || 'Submission failed'); }
    finally { setSubmitting(false); }
  };

  // ── Key Entry ─────────────────────────────────────────────
  if (step === 'key') return (
    <div className="page-content animate-in" style={{ maxWidth: 480, margin: '0 auto' }}>
      <ToastContainer toasts={toasts} />
      <div style={{ textAlign: 'center', padding: '40px 0 32px' }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 30, fontWeight: 700, color: 'var(--field)', fontFamily: 'var(--font-display)' }}>S</div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 30, marginBottom: 8 }}>Welcome to Soch</h1>
        <p style={{ color: 'var(--ink-muted)', fontSize: 15 }}>Enter your onboarding key from your joining email</p>
      </div>
      <div className="card" style={{ padding: 32 }}>
        <Input label="Access Key" placeholder="SOCH-XXXX-XXXX-XXXX-XXXX"
          value={key} onChange={e => setKey(e.target.value.toUpperCase())}
          onKeyDown={e => e.key === 'Enter' && verifyKey()}
          hint="You received this key in your offer / joining email from HR"
          style={{ textAlign: 'center', letterSpacing: 3, fontSize: 16, fontWeight: 600 }} />
        <Button variant="primary" size="lg" className="w-full" style={{ marginTop: 20 }}
          onClick={() => verifyKey()} loading={loading} disabled={!key.trim()}>
          Access My Onboarding
        </Button>
      </div>
    </div>
  );

  // ── Done ──────────────────────────────────────────────────
  if (step === 'done') return (
    <div className="page-content animate-in" style={{ maxWidth: 520, margin: '0 auto', textAlign: 'center' }}>
      <div style={{ padding: '60px 40px' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 30, marginBottom: 12 }}>You're all set!</h2>
        <p style={{ color: 'var(--ink-muted)', fontSize: 15, lineHeight: 1.7, marginBottom: 24 }}>
          Welcome to <strong>Soch</strong>, {data?.applicant?.name?.split(' ')[0]}! Your onboarding is complete.
          The HR team will be in touch with your joining schedule.
        </p>
        <div className="card" style={{ textAlign: 'left' }}>
          <div style={{ fontSize: 13, color: 'var(--ink-muted)', lineHeight: 2 }}>
            <div><strong>{data?.applicant?.name}</strong></div>
            <div><strong>{data?.job?.title}</strong></div>
            <div><strong>{data?.job?.department}</strong> · {data?.job?.region}</div>
          </div>
        </div>
      </div>
    </div>
  );

  const tpl = data?.template;
  const requiredDocs = tpl?.requiredDocs || [];

  // ── Form ──────────────────────────────────────────────────
  return (
    <div className="page-content animate-in" style={{ maxWidth: 720, margin: '0 auto' }}>
      <ToastContainer toasts={toasts} />

      {/* Header */}
      <div style={{ background: 'var(--ink)', color: 'white', borderRadius: 'var(--radius-lg)', padding: '28px 36px', marginBottom: 28 }}>
        <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Soch Onboarding</p>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 26, margin: 0 }}>Hello, {data?.applicant?.name?.split(' ')[0]}!</h2>
        <p style={{ color: 'rgba(255,255,255,0.65)', marginTop: 6, fontSize: 14 }}>{data?.job?.title} · {data?.job?.department}</p>
      </div>

      {/* Template-driven form fields */}
      {tpl && tpl.fields?.length > 0 && (
        <div className="card" style={{ padding: 28, marginBottom: 16 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 20 }}>Joining Details</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {tpl.fields.map(field => (
              <FieldRenderer key={field.id} field={field}
                value={formValues[field.id] || ''}
                fileUpload={fileUploads[field.id]}
                uploading={uploadingField === field.id}
                onChange={val => set(field.id, val)}
                onFileUpload={file => uploadFile(field.id, file)} />
            ))}
          </div>
        </div>
      )}

      {/* Required documents to acknowledge */}
      {requiredDocs.length > 0 && (
        <div className="card" style={{ padding: 28, marginBottom: 16 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 8 }}>Documents to Read & Acknowledge</h3>
          <p style={{ fontSize: 13, color: 'var(--ink-muted)', marginBottom: 20 }}>
            Please read each document fully. You can acknowledge only after the minimum read time has passed.
          </p>
          {requiredDocs.map(doc => (
            <DocAcknowledgeCard key={doc.id} doc={doc}
              acknowledged={acknowledgedDocs.includes(doc.id)}
              apiKey={key.trim().toUpperCase()}
              onAcknowledged={(docId) => setAcknowledgedDocs(p => [...p, docId])}
              toast={toast}
            />
          ))}
        </div>
      )}

      {/* Assignment tasks */}
      {tasks.length > 0 && (
        <div className="card" style={{ padding: 28, marginBottom: 16 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 8 }}>Tasks to Complete</h3>
          <p style={{ fontSize: 13, color: 'var(--ink-muted)', marginBottom: 20 }}>
            Complete all required tasks before submitting your onboarding.
          </p>
          {tasks.map(task => (
            <EmployeeTaskCard
              key={task._id}
              task={task}
              apiKey={key.trim().toUpperCase()}
              onUpdated={() => loadTasks()}
              toast={toast}
            />
          ))}
        </div>
      )}

      {/* Personal Information (fills joiningKit) */}
      <JoiningKitSection apiKey={key.trim().toUpperCase()} applicant={data?.applicant} toast={toast} />

      {/* Statutory Forms */}
      <StatutoryFormsSection apiKey={key.trim().toUpperCase()} applicant={data?.applicant} job={data?.job} toast={toast} />

      {/* Submit */}
      <div className="card" style={{ padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <p style={{ fontSize: 13, color: 'var(--ink-muted)', margin: 0 }}>
          All information will be shared securely with the Soch HR team.
        </p>
        <Button variant="primary" size="lg" onClick={handleSubmit} loading={submitting} style={{ flexShrink: 0 }}>
          Submit & Complete Onboarding 
        </Button>
      </div>
    </div>
  );
}

// ── Joining Kit, Candidate Section ──────────────────────────
function JoiningKitSection({ apiKey, applicant, toast }) {
  const kf = loadKF(); // read KF once at init time
  const blankPersonal = {
    fullName:         applicant?.name   || kf.fullName         || '',
    dateOfBirth:      kf.dateOfBirth    || '',
    gender:           kf.gender         || '',
    fatherName:       kf.fatherName     || '',
    motherName:       kf.motherName     || '',
    spouseName:       kf.spouseName     || '',
    bloodGroup:       '',
    panNumber:        kf.panNumber      || '',
    aadhaarNumber:    kf.aadhaarNumber  || '',
    permanentAddress: kf.permanentAddress || '',
    currentAddress:   '',
    mobileNo:         applicant?.mobile || kf.mobileNumber     || '',
    emailId:          applicant?.email  || kf.emailId          || '',
    marriageDate:     '',
    emergencyContacts: [{ name: '', number: '', relationship: '' }],
  };
  const blankEdu = [
    { level: '10th', schoolName: '', yearOfPassing: '', board: '', percentage: '' },
    { level: '12th', schoolName: '', yearOfPassing: '', board: '', percentage: '' },
    { level: 'Degree / Diploma', schoolName: '', yearOfPassing: '', board: '', percentage: '' },
  ];
  const blankWork = [{ companyName: '', dateFrom: '', dateTo: '', designation: '', industry: '', reasonForLeaving: '' }];

  const [personal, setPersonal] = useState(blankPersonal);
  const [education, setEducation] = useState(blankEdu);
  const [workHistory, setWorkHistory] = useState(blankWork);
  const [workedWithSoch, setWorkedWithSoch] = useState(false);
  const [sochDetails, setSochDetails] = useState('');
  const [activeTab, setActiveTab] = useState('personal');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const STEP_TABS = ['personal', 'education', 'work'];
  const stepIdx = STEP_TABS.indexOf(activeTab);

  // Seed KF from applicant on first render (before any form is filled)
  useEffect(() => {
    if (applicant?.name)   saveKF({ fullName: applicant.name });
    if (applicant?.mobile) saveKF({ mobileNumber: applicant.mobile });
    if (applicant?.email)  saveKF({ emailId: applicant.email });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetch(`${API}/onboarding/employee/${apiKey}/statutory/joiningKit`)
      .then(r => r.json()).then(d => {
        if (d && Object.keys(d).length > 0) {
          setPersonal(prev => ({ ...blankPersonal, ...d }));
          if (d.education?.length) setEducation(d.education);
          if (d.workHistory?.length) setWorkHistory(d.workHistory);
          if (d.workedWithSoch !== undefined) setWorkedWithSoch(d.workedWithSoch);
          if (d.sochDetails) setSochDetails(d.sochDetails);
          // Populate KF from previously saved joining kit data
          saveKF({
            fullName:         d.fullName,
            dateOfBirth:      d.dateOfBirth,
            gender:           d.gender,
            fatherName:       d.fatherName,
            motherName:       d.motherName,
            spouseName:       d.spouseName,
            aadhaarNumber:    d.aadhaarNumber,
            panNumber:        d.panNumber,
            mobileNumber:     d.mobileNo,
            emailId:          d.emailId,
            permanentAddress: d.permanentAddress,
          });
        }
        setLoaded(true);
      }).catch(() => setLoaded(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  // setP with live KF update for tracked fields
  const KF_TRACKED = { fullName: 'fullName', dateOfBirth: 'dateOfBirth', gender: 'gender',
    fatherName: 'fatherName', motherName: 'motherName', spouseName: 'spouseName',
    aadhaarNumber: 'aadhaarNumber', panNumber: 'panNumber',
    mobileNo: 'mobileNumber', emailId: 'emailId', permanentAddress: 'permanentAddress' };
  const setP = (k, v) => {
    if (KF_TRACKED[k]) saveKF({ [KF_TRACKED[k]]: v });
    setPersonal(p => ({ ...p, [k]: v }));
  };
  const setEmergency = (i, field, val) => setPersonal(p => {
    const arr = [...(p.emergencyContacts || [])];
    arr[i] = { ...arr[i], [field]: val };
    return { ...p, emergencyContacts: arr };
  });
  const setEdu = (i, field, val) => setEducation(p => { const a = [...p]; a[i] = { ...a[i], [field]: val }; return a; });
  const setWork = (i, field, val) => setWorkHistory(p => { const a = [...p]; a[i] = { ...a[i], [field]: val }; return a; });

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...personal, education, workHistory, workedWithSoch, sochDetails };
      const res = await fetch(`${API}/onboarding/employee/${apiKey}/statutory/joiningKit`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error((await res.json()).error);
      toast.success('Personal information saved ');
    } catch (err) { toast.error(err.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const TABS = [
    { id: 'personal',  label: 'Personal Details' },
    { id: 'education', label: 'Education' },
    { id: 'work',      label: 'Work History' },
  ];

  if (!loaded) return null;

  return (
    <div className="card" style={{ padding: 28, marginBottom: 16 }}>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 6 }}>Personal Information</h3>
      <p style={{ fontSize: 13, color: 'var(--ink-muted)', marginBottom: 20 }}>
        This information will be used for your employment records, salary processing, and statutory registrations.
      </p>

      {/* Step indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 24 }}>
        {TABS.map((t, i) => (
          <React.Fragment key={t.id}>
            <button onClick={() => setActiveTab(t.id)} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', border: 'none', background: 'none', cursor: 'pointer',
              fontFamily: 'var(--font-body)', fontSize: 13,
              fontWeight: activeTab === t.id ? 700 : 400,
              color: activeTab === t.id ? 'var(--ink)' : STEP_TABS.indexOf(activeTab) > i ? 'var(--sage)' : 'var(--ink-muted)',
            }}>
              <span style={{
                width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, flexShrink: 0,
                background: activeTab === t.id ? 'var(--ink)' : STEP_TABS.indexOf(activeTab) > i ? 'var(--sage)' : 'var(--canvas-warm)',
                color: activeTab === t.id || STEP_TABS.indexOf(activeTab) > i ? 'white' : 'var(--ink-muted)',
                border: '1.5px solid',
                borderColor: activeTab === t.id ? 'var(--ink)' : STEP_TABS.indexOf(activeTab) > i ? 'var(--sage)' : 'var(--border)',
              }}>{i + 1}</span>
              {t.label.replace(/^[^\w]*/, '')}
            </button>
            {i < TABS.length - 1 && <div style={{ flex: 1, height: 1, background: 'var(--border)', minWidth: 16 }} />}
          </React.Fragment>
        ))}
      </div>

      {/* ── Personal Details ── */}
      {activeTab === 'personal' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {[
              ['Full Name', 'fullName'],
              ['Date of Birth', 'dateOfBirth', 'date'],
              ['Gender', 'gender'],
              ['Father\'s Name', 'fatherName'],
              ['Mother\'s Name', 'motherName'],
              ['Blood Group', 'bloodGroup'],
              ['PAN Number', 'panNumber'],
              ['Aadhaar Number', 'aadhaarNumber'],
              ['Mobile No.', 'mobileNo'],
              ['Email ID', 'emailId', 'email'],
              ['Marriage Date (if applicable)', 'marriageDate', 'date'],
              ['Spouse Name (if applicable)', 'spouseName'],
            ].map(([label, key, type]) => (
              <FRow key={key} label={label}>
                {key === 'gender' ? (
                  <FSelect value={personal.gender} onChange={v => setP('gender', v)}
                    options={['Male', 'Female', 'Other', 'Prefer not to say']} />
                ) : key === 'bloodGroup' ? (
                  <FSelect value={personal.bloodGroup} onChange={v => setP('bloodGroup', v)}
                    options={['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']} />
                ) : (
                  <FInput value={personal[key]} onChange={v => setP(key, v)} type={type || 'text'} />
                )}
              </FRow>
            ))}
          </div>
          <div style={{ marginTop: 8 }}>
            <FRow label="Permanent Address">
              <textarea className="form-input form-textarea" rows={2} value={personal.permanentAddress || ''}
                onChange={e => setP('permanentAddress', e.target.value)} placeholder="House/Flat No., Street, City, State, Pincode" />
            </FRow>
            <FRow label="Current / Correspondence Address">
              <div style={{ marginBottom: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer', color: 'var(--ink-muted)' }}>
                  <input type="checkbox"
                    checked={personal.currentAddress === personal.permanentAddress && !!personal.permanentAddress}
                    onChange={e => { if (e.target.checked) setP('currentAddress', personal.permanentAddress); }} />
                  Same as permanent address
                </label>
              </div>
              <textarea className="form-input form-textarea" rows={2} value={personal.currentAddress || ''}
                onChange={e => setP('currentAddress', e.target.value)} placeholder="House/Flat No., Street, City, State, Pincode" />
            </FRow>
          </div>
          <SectionHead label="Emergency Contacts" />
          {(personal.emergencyContacts || []).map((ec, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 10, marginBottom: 10, alignItems: 'end' }}>
              <FRow label="Name"><FInput value={ec.name} onChange={v => setEmergency(i, 'name', v)} /></FRow>
              <FRow label="Mobile Number"><FInput value={ec.number} onChange={v => setEmergency(i, 'number', v)} /></FRow>
              <FRow label="Relationship"><FInput value={ec.relationship} onChange={v => setEmergency(i, 'relationship', v)} /></FRow>
              {i > 0 && (
                <button onClick={() => setPersonal(p => ({ ...p, emergencyContacts: p.emergencyContacts.filter((_, j) => j !== i) }))}
                  style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', paddingBottom: 8, fontSize: 13 }}>Remove</button>
              )}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setPersonal(p => ({ ...p, emergencyContacts: [...(p.emergencyContacts || []), { name: '', number: '', relationship: '' }] }))}>
            + Add Emergency Contact
          </Button>
        </div>
      )}

      {/* ── Education ── */}
      {activeTab === 'education' && (
        <div>
          <p style={{ fontSize: 13, color: 'var(--ink-muted)', marginBottom: 16 }}>
            Please fill in your educational qualifications starting from 10th standard.
          </p>
          {education.map((row, i) => (
            <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px', marginBottom: 12, background: 'var(--field)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <strong style={{ fontSize: 13 }}>{row.level || `Qualification ${i + 1}`}</strong>
                {i >= 3 && (
                  <button onClick={() => setEducation(p => p.filter((_, j) => j !== i))}
                    style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12 }}>Remove</button>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {i >= 3 && <FRow label="Level / Course"><FInput value={row.level} onChange={v => setEdu(i, 'level', v)} placeholder="e.g. MBA, PG Diploma" /></FRow>}
                <FRow label="School / College / University"><FInput value={row.schoolName} onChange={v => setEdu(i, 'schoolName', v)} /></FRow>
                <FRow label="Year of Passing"><FInput value={row.yearOfPassing} onChange={v => setEdu(i, 'yearOfPassing', v)} placeholder="e.g. 2018" /></FRow>
                <FRow label="Board / University"><FInput value={row.board} onChange={v => setEdu(i, 'board', v)} placeholder="e.g. CBSE, Mumbai University" /></FRow>
                <FRow label="Percentage / Grade / CGPA"><FInput value={row.percentage} onChange={v => setEdu(i, 'percentage', v)} placeholder="e.g. 78% or 7.5 CGPA" /></FRow>
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm"
            onClick={() => setEducation(p => [...p, { level: '', schoolName: '', yearOfPassing: '', board: '', percentage: '' }])}>
            + Add Qualification
          </Button>
        </div>
      )}

      {/* ── Work History ── */}
      {activeTab === 'work' && (
        <div>
          <p style={{ fontSize: 13, color: 'var(--ink-muted)', marginBottom: 16 }}>
            List your previous employers, most recent first. Leave blank if this is your first job.
          </p>
          {workHistory.map((row, i) => (
            <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px', marginBottom: 12, background: 'var(--field)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <strong style={{ fontSize: 13 }}>Employer {i + 1}, Latest First</strong>
                {i > 0 && (
                  <button onClick={() => setWorkHistory(p => p.filter((_, j) => j !== i))}
                    style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12 }}>Remove</button>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FRow label="Company Name"><FInput value={row.companyName} onChange={v => setWork(i, 'companyName', v)} /></FRow>
                <FRow label="Designation"><FInput value={row.designation} onChange={v => setWork(i, 'designation', v)} /></FRow>
                <FRow label="From"><FInput type="date" value={row.dateFrom} onChange={v => setWork(i, 'dateFrom', v)} /></FRow>
                <FRow label="To"><FInput type="date" value={row.dateTo} onChange={v => setWork(i, 'dateTo', v)} /></FRow>
                <FRow label="Industry"><FInput value={row.industry} onChange={v => setWork(i, 'industry', v)} placeholder="e.g. Retail, IT, Banking" /></FRow>
                <FRow label="Reason for Leaving"><FInput value={row.reasonForLeaving} onChange={v => setWork(i, 'reasonForLeaving', v)} /></FRow>
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm"
            onClick={() => setWorkHistory(p => [...p, { companyName: '', dateFrom: '', dateTo: '', designation: '', industry: '', reasonForLeaving: '' }])}>
            + Add Previous Employer
          </Button>
          <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--canvas-warm)', borderRadius: 8 }}>
            <FCheck
              label="I have previously worked with Soch or any MD Retail affiliated company"
              checked={workedWithSoch}
              onChange={setWorkedWithSoch}
            />
            {workedWithSoch && (
              <div style={{ marginTop: 10 }}>
                <FRow label="Please provide details (store, duration, designation)">
                  <textarea className="form-input form-textarea" rows={2} value={sochDetails}
                    onChange={e => setSochDetails(e.target.value)} />
                </FRow>
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button variant="outline" disabled={stepIdx === 0} onClick={() => setActiveTab(STEP_TABS[stepIdx - 1])}>
          Back
        </Button>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="ghost" onClick={handleSave} loading={saving}>
            Save
          </Button>
          {stepIdx < STEP_TABS.length - 1 ? (
            <Button variant="primary" onClick={async () => { await handleSave(); setActiveTab(STEP_TABS[stepIdx + 1]); }}>
              Next
            </Button>
          ) : (
            <Button variant="primary" onClick={handleSave} loading={saving}>
              Save & Finish 
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Statutory Forms Section ───────────────────────────────────
function StatutoryFormsSection({ apiKey, applicant, job, toast }) {
  const [activeForm, setActiveForm] = useState(null);

  const forms = [
    { id: 'epfForm',     label: 'EPF Form 2',       icon: '', desc: 'Provident Fund nominee declaration' },
    { id: 'esicForm',    label: 'ESIC Registration', icon: '', desc: 'Employee State Insurance registration' },
    { id: 'gratuityForm',label: 'Gratuity Form F',   icon: '', desc: 'Gratuity nominee declaration' },
  ];

  return (
    <div className="card" style={{ padding: 28, marginBottom: 16 }}>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 6 }}>Statutory Forms</h3>
      <p style={{ fontSize: 13, color: 'var(--ink-muted)', marginBottom: 20 }}>
        Please complete all government-mandated forms below. Your data is saved automatically.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {forms.map(f => (
          <div key={f.id} style={{
            border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
            padding: '14px 18px', background: 'var(--canvas-warm)',
            display: 'flex', alignItems: 'center', gap: 14
          }}>
            <div style={{ fontSize: 26 }}>{f.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{f.label}</div>
              <div style={{ fontSize: 12, color: 'var(--ink-muted)' }}>{f.desc}</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setActiveForm(f.id)}>
              Open Form
            </Button>
          </div>
        ))}
      </div>

      {activeForm === 'epfForm' && (
        <EPFForm2Modal apiKey={apiKey} applicant={applicant} onClose={() => setActiveForm(null)} toast={toast} />
      )}
      {activeForm === 'esicForm' && (
        <ESICFormModal apiKey={apiKey} applicant={applicant} job={job} onClose={() => setActiveForm(null)} toast={toast} />
      )}
      {activeForm === 'gratuityForm' && (
        <GratuityFormModal apiKey={apiKey} applicant={applicant} job={job} onClose={() => setActiveForm(null)} toast={toast} />
      )}
    </div>
  );
}

// ── Shared helpers ────────────────────────────────────────────
function FRow({ label, children, required }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}{required && <span style={{ color: 'var(--accent)' }}> *</span>}</label>
      {children}
    </div>
  );
}
function FDateInput({ value, onChange }) {
  const toDisplay = (iso) => {
    if (!iso) return '';
    // Already in DD/MM/YYYY form?
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(iso)) return iso;
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
  };
  const [display, setDisplay] = React.useState(() => toDisplay(value));

  // Sync display whenever the value prop changes (e.g. server data loads)
  React.useEffect(() => {
    const expected = toDisplay(value);
    setDisplay(prev => {
      // Don't clobber while user is actively typing a partial date
      if (prev.length > 0 && prev.length < 10 && !value) return prev;
      return expected;
    });
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const raw = e.target.value;
    // Strip everything except digits
    const digits = raw.replace(/[^\d]/g, '').slice(0, 8);
    let fmt = digits;
    if (digits.length > 4) fmt = digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4);
    else if (digits.length > 2) fmt = digits.slice(0, 2) + '/' + digits.slice(2);
    setDisplay(fmt);
    const m = fmt.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (m) onChange(`${m[3]}-${m[2]}-${m[1]}`);
    else if (!fmt) onChange('');
  };
  return <input className="form-input" type="text" inputMode="numeric" value={display}
    onChange={handleChange} placeholder="DD/MM/YYYY" maxLength={10} />;
}
function FInput({ value, onChange, placeholder, type = 'text' }) {
  if (type === 'date') return <FDateInput value={value} onChange={onChange} />;
  return <input className="form-input" type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder || ''} />;
}
function FSelect({ value, onChange, options }) {
  return (
    <select className="form-input form-select" value={value || ''} onChange={e => onChange(e.target.value)}>
      <option value="">Select…</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
function FCheck({ label, checked, onChange }) {
  return (
    <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

function ModalShell({ title, subtitle, onClose, onSave, saving, children }) {
  const ReactDOM = require('react-dom');
  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(26,20,16,0.6)', zIndex: 9999, overflowY: 'auto', padding: '20px' }}>
      <div style={{ background: 'var(--canvas-card)', borderRadius: 'var(--radius-lg)', padding: '28px 32px', maxWidth: 780, margin: '0 auto 60px', boxShadow: 'var(--shadow-lg)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 22, margin: 0 }}>{title}</h3>
            {subtitle && <p style={{ fontSize: 13, color: 'var(--ink-muted)', margin: '4px 0 0' }}>{subtitle}</p>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--ink-muted)' }}>Close</button>
        </div>
        {children}
        <div style={{ display: 'flex', gap: 10, paddingTop: 20, borderTop: '1px solid var(--border)', marginTop: 20 }}>
          <Button variant="primary" size="lg" onClick={onSave} loading={saving} style={{ flex: 1 }}>Save & Close</Button>
          <Button variant="outline" size="lg" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function SectionHead({ label }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
      color: 'var(--ink-muted)', borderBottom: '1px solid var(--border)', paddingBottom: 6, marginBottom: 14, marginTop: 20 }}>
      {label}
    </div>
  );
}

// ── EPF Form 2 ────────────────────────────────────────────────
function EPFForm2Modal({ apiKey, applicant, onClose, toast }) {
  const [data, setData] = useState({
    partA_nominees: [{ name: '', address: '', relationship: '', age: '', proportion: '', guardianName: '', guardianRelationship: '', guardianAddress: '' }],
    partA_certifiedNoFamily: false,
    partA_fatherMotherDependent: false,
    partB_familyMembers: [{ nameAddress: '', age: '', relationship: '' }],
    partB_certifiedNoFamily: false,
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(`${API}/onboarding/employee/${apiKey}/statutory/epfForm`)
      .then(r => r.json()).then(d => {
        if (d && Object.keys(d).length > 0) setData(prev => ({ ...prev, ...d }));
        setLoaded(true);
      }).catch(() => setLoaded(true));
  }, [apiKey]);

  const set = (key, val) => setData(p => ({ ...p, [key]: val }));
  const setNominee = (i, field, val) => setData(p => {
    const arr = [...(p.partA_nominees || [])];
    arr[i] = { ...arr[i], [field]: val };
    return { ...p, partA_nominees: arr };
  });
  const setFamilyMember = (i, field, val) => setData(p => {
    const arr = [...(p.partB_familyMembers || [])];
    arr[i] = { ...arr[i], [field]: val };
    return { ...p, partB_familyMembers: arr };
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/onboarding/employee/${apiKey}/statutory/epfForm`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, signedAt: new Date().toISOString() })
      });
      if (!res.ok) throw new Error((await res.json()).error);
      toast.success('EPF Form 2 saved ');
      onClose();
    } catch (err) { toast.error(err.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (!loaded) return null;

  return (
    <ModalShell title="EPF Form 2, Nomination & Declaration"
      subtitle="Employees' Provident Fund Scheme 1952 · Paragraphs 33 & 61(1)"
      onClose={onClose} onSave={handleSave} saving={saving}>

      <div style={{ background: 'var(--canvas-warm)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: 'var(--ink-muted)' }}>
        <strong>Member:</strong> {applicant?.name} &nbsp;|&nbsp; <strong>Account No.:</strong> (to be assigned by HR)
      </div>

      <SectionHead label="Part A, EPF Nominees" />
      <p style={{ fontSize: 13, color: 'var(--ink-muted)', marginBottom: 14 }}>
        Nominate person(s) to receive the amount standing to your credit in the Employees' Provident Fund in the event of your death.
      </p>
      {(data.partA_nominees || []).map((n, i) => (
        <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px', marginBottom: 12, background: 'var(--field)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <strong style={{ fontSize: 13 }}>Nominee {i + 1}</strong>
            {i > 0 && <button onClick={() => setData(p => ({ ...p, partA_nominees: p.partA_nominees.filter((_, j) => j !== i) }))}
              style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12 }}>Remove</button>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FRow label="Name"><FInput value={n.name} onChange={v => setNominee(i, 'name', v)} placeholder="Full name" /></FRow>
            <FRow label="Relationship"><FInput value={n.relationship} onChange={v => setNominee(i, 'relationship', v)} placeholder="e.g. Spouse, Child" /></FRow>
            <FRow label="Age"><FInput value={n.age} onChange={v => setNominee(i, 'age', v)} placeholder="Age" /></FRow>
            <FRow label="Share / Proportion (%)"><FInput value={n.proportion} onChange={v => setNominee(i, 'proportion', v)} placeholder="e.g. 100% or 50%" /></FRow>
          </div>
          <FRow label="Address"><textarea className="form-input form-textarea" rows={2} value={n.address || ''} onChange={e => setNominee(i, 'address', e.target.value)} placeholder="Full address" /></FRow>
          <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--canvas-warm)', borderRadius: 6 }}>
            <div style={{ fontSize: 12, color: 'var(--ink-muted)', marginBottom: 8 }}>If nominee is a minor, Guardian details</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FRow label="Guardian Name"><FInput value={n.guardianName} onChange={v => setNominee(i, 'guardianName', v)} /></FRow>
              <FRow label="Guardian Relationship"><FInput value={n.guardianRelationship} onChange={v => setNominee(i, 'guardianRelationship', v)} /></FRow>
            </div>
            <FRow label="Guardian Address"><FInput value={n.guardianAddress} onChange={v => setNominee(i, 'guardianAddress', v)} /></FRow>
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => setData(p => ({ ...p, partA_nominees: [...(p.partA_nominees || []), { name: '', address: '', relationship: '', age: '', proportion: '', guardianName: '', guardianRelationship: '', guardianAddress: '' }] }))}>
        + Add Nominee
      </Button>

      <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <FCheck label="Certified that I have no family as defined in Para 2(g) of the EPF Scheme 1952" checked={data.partA_certifiedNoFamily} onChange={v => set('partA_certifiedNoFamily', v)} />
        <FCheck label="Certified that my father/mother is/are dependent upon me" checked={data.partA_fatherMotherDependent} onChange={v => set('partA_fatherMotherDependent', v)} />
      </div>

      <SectionHead label="Part B, EPS (Pension Scheme) Family Members" />
      <p style={{ fontSize: 13, color: 'var(--ink-muted)', marginBottom: 14 }}>
        Particulars of family members eligible to receive Widow/Children Pension in the event of your death.
      </p>
      {(data.partB_familyMembers || []).map((m, i) => (
        <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', marginBottom: 10, background: 'var(--field)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <strong style={{ fontSize: 13 }}>Family Member {i + 1}</strong>
            {i > 0 && <button onClick={() => setData(p => ({ ...p, partB_familyMembers: p.partB_familyMembers.filter((_, j) => j !== i) }))}
              style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12 }}>Remove</button>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <FRow label="Name & Address"><FInput value={m.nameAddress} onChange={v => setFamilyMember(i, 'nameAddress', v)} /></FRow>
            <FRow label="Age"><FInput value={m.age} onChange={v => setFamilyMember(i, 'age', v)} /></FRow>
            <FRow label="Relationship"><FInput value={m.relationship} onChange={v => setFamilyMember(i, 'relationship', v)} /></FRow>
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => setData(p => ({ ...p, partB_familyMembers: [...(p.partB_familyMembers || []), { nameAddress: '', age: '', relationship: '' }] }))}>
        + Add Family Member
      </Button>
      <div style={{ marginTop: 12 }}>
        <FCheck label="Certified that I have no family as defined in Para 2(vii) of the EPS 1971" checked={data.partB_certifiedNoFamily} onChange={v => set('partB_certifiedNoFamily', v)} />
      </div>
    </ModalShell>
  );
}

// ── ESIC Form ─────────────────────────────────────────────────
function ESICFormModal({ apiKey, applicant, job, onClose, toast }) {
  const kf = loadKF();
  const [data, setData] = useState({
    fullName:     kf.fullName     || applicant?.name   || '',
    hasExistingEsic: false, esicNumber: '',
    gender:       kf.gender       || '',
    dateOfBirth:  kf.dateOfBirth  || '',
    maritalStatus: kf.maritalStatus || '',
    fatherName:   kf.fatherName   || '',
    spouseName:   kf.spouseName   || '',
    mobileNumber: kf.mobileNumber || applicant?.mobile || '',
    emailId:      kf.emailId      || applicant?.email  || '',
    aadhaarNumber: kf.aadhaarNumber || '',
    panNumber:    kf.panNumber    || '',
    educationalQualification: '',
    dateOfJoining: '', department: job?.department || '', designation: '',
    monthlyGrossSalary: '',
    presentAddress: { houseDoorNo: '', streetArea: '', landmark: '', city: '', district: '', state: '', pincode: '' },
    familySameAsPresent: false,
    familyAddress: { houseDoorNo: '', streetArea: '', landmark: '', city: '', district: '', state: '', pincode: '' },
    familyMembers: [],
    bankName: '', accountHolderName: applicant?.name || '', accountNumber: '', ifscCode: '', branch: '',
    nomineeName: '', nomineeRelationship: '', nomineeDateOfBirth: '', nomineeAadhaar: '', nomineeContact: '',
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(`${API}/onboarding/employee/${apiKey}/statutory/esicForm`)
      .then(r => r.json()).then(d => {
        if (d && Object.keys(d).length > 0) setData(prev => ({ ...prev, ...d }));
        setLoaded(true);
      }).catch(() => setLoaded(true));
  }, [apiKey]);

  // Save cross-form fields live as user types
  const KF_ESIC = { fatherName: 'fatherName', maritalStatus: 'maritalStatus', spouseName: 'spouseName',
    gender: 'gender', dateOfBirth: 'dateOfBirth', aadhaarNumber: 'aadhaarNumber', panNumber: 'panNumber',
    mobileNumber: 'mobileNumber', emailId: 'emailId', fullName: 'fullName' };
  const set = (key, val) => {
    if (KF_ESIC[key]) saveKF({ [KF_ESIC[key]]: val });
    setData(p => ({ ...p, [key]: val }));
  };
  const setAddr = (type, field, val) => setData(p => ({ ...p, [type]: { ...(p[type] || {}), [field]: val } }));
  const setFM = (i, field, val) => setData(p => {
    const arr = [...(p.familyMembers || [])];
    arr[i] = { ...arr[i], [field]: val };
    return { ...p, familyMembers: arr };
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...data };
      if (data.familySameAsPresent) payload.familyAddress = data.presentAddress;
      const res = await fetch(`${API}/onboarding/employee/${apiKey}/statutory/esicForm`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, declarationSignedAt: new Date().toISOString() })
      });
      if (!res.ok) throw new Error((await res.json()).error);
      toast.success('ESIC Form saved ');
      onClose();
    } catch (err) { toast.error(err.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (!loaded) return null;

  const addrFields = [
    ['houseDoorNo', 'House / Door No.'], ['streetArea', 'Street / Area'],
    ['landmark', 'Landmark'], ['city', 'City'], ['district', 'District'],
    ['state', 'State'], ['pincode', 'Pincode'],
  ];

  return (
    <ModalShell title="ESIC, New Employee Registration"
      subtitle="Employees' State Insurance, Insured Person Data Collection Form"
      onClose={onClose} onSave={handleSave} saving={saving}>

      <SectionHead label="A. Employee Basic Details" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FRow label="Full Name (as per Aadhaar)" required><FInput value={data.fullName} onChange={v => set('fullName', v)} /></FRow>
        <FRow label="Date of Birth" required><FInput type="date" value={data.dateOfBirth} onChange={v => set('dateOfBirth', v)} /></FRow>
        <FRow label="Gender"><FSelect value={data.gender} onChange={v => set('gender', v)} options={['Male', 'Female', 'Other']} /></FRow>
        <FRow label="Marital Status"><FSelect value={data.maritalStatus} onChange={v => set('maritalStatus', v)} options={['Single', 'Married', 'Other']} /></FRow>
        <FRow label="Father's Name"><FInput value={data.fatherName} onChange={v => set('fatherName', v)} /></FRow>
        <FRow label="Spouse Name (if applicable)"><FInput value={data.spouseName} onChange={v => set('spouseName', v)} /></FRow>
        <FRow label="Mobile Number" required><FInput value={data.mobileNumber} onChange={v => set('mobileNumber', v)} /></FRow>
        <FRow label="Email ID"><FInput type="email" value={data.emailId} onChange={v => set('emailId', v)} /></FRow>
        <FRow label="Aadhaar Number" required><FInput value={data.aadhaarNumber} onChange={v => set('aadhaarNumber', v)} /></FRow>
        <FRow label="PAN Number"><FInput value={data.panNumber} onChange={v => set('panNumber', v)} /></FRow>
        <FRow label="Educational Qualification"><FInput value={data.educationalQualification} onChange={v => set('educationalQualification', v)} /></FRow>
        <FRow label="Date of Joining"><FInput type="date" value={data.dateOfJoining} onChange={v => set('dateOfJoining', v)} /></FRow>
        <FRow label="Department"><FInput value={data.department} onChange={v => set('department', v)} /></FRow>
        <FRow label="Designation"><FInput value={data.designation} onChange={v => set('designation', v)} /></FRow>
        <FRow label="Monthly Gross Salary (₹)"><FInput value={data.monthlyGrossSalary} onChange={v => set('monthlyGrossSalary', v)} /></FRow>
      </div>
      <div style={{ marginTop: 8 }}>
        <FCheck label="Do you already have an ESIC Number?" checked={data.hasExistingEsic} onChange={v => set('hasExistingEsic', v)} />
        {data.hasExistingEsic && <div style={{ marginTop: 8 }}><FRow label="Existing ESIC Number"><FInput value={data.esicNumber} onChange={v => set('esicNumber', v)} /></FRow></div>}
      </div>

      <SectionHead label="B. Present Residential Address" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {addrFields.map(([f, l]) => <FRow key={f} label={l}><FInput value={data.presentAddress?.[f]} onChange={v => setAddr('presentAddress', f, v)} /></FRow>)}
      </div>

      <SectionHead label="C. Family Address (if dependants are outstation)" />
      <FCheck label="Same as present address" checked={data.familySameAsPresent} onChange={v => set('familySameAsPresent', v)} />
      {!data.familySameAsPresent && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
          {addrFields.map(([f, l]) => <FRow key={f} label={l}><FInput value={data.familyAddress?.[f]} onChange={v => setAddr('familyAddress', f, v)} /></FRow>)}
        </div>
      )}

      <SectionHead label="D. Family / Dependant Details (for ESIC Medical Coverage)" />
      {(data.familyMembers || []).map((m, i) => (
        <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', marginBottom: 10, background: 'var(--field)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <strong style={{ fontSize: 13 }}>Dependant {i + 1}</strong>
            <button onClick={() => setData(p => ({ ...p, familyMembers: p.familyMembers.filter((_, j) => j !== i) }))}
              style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12 }}>Remove</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <FRow label="Name (as per Aadhaar)"><FInput value={m.name} onChange={v => setFM(i, 'name', v)} /></FRow>
            <FRow label="Relationship"><FInput value={m.relationship} onChange={v => setFM(i, 'relationship', v)} /></FRow>
            <FRow label="Date of Birth"><FInput type="date" value={m.dateOfBirth} onChange={v => setFM(i, 'dateOfBirth', v)} /></FRow>
            <FRow label="Gender"><FSelect value={m.gender} onChange={v => setFM(i, 'gender', v)} options={['Male', 'Female', 'Other']} /></FRow>
            <FRow label="Aadhaar No."><FInput value={m.aadhaarNo} onChange={v => setFM(i, 'aadhaarNo', v)} /></FRow>
            <FRow label="Residing with Employee?"><FSelect value={m.residingWithEmployee === true ? 'Yes' : m.residingWithEmployee === false ? 'No' : ''} onChange={v => setFM(i, 'residingWithEmployee', v === 'Yes')} options={['Yes', 'No']} /></FRow>
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => setData(p => ({ ...p, familyMembers: [...(p.familyMembers || []), { name: '', relationship: '', dateOfBirth: '', gender: '', aadhaarNo: '', residingWithEmployee: true }] }))}>
        + Add Dependant
      </Button>

      <SectionHead label="E. Bank Details (for Sickness / Maternity Benefit)" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FRow label="Bank Name"><FInput value={data.bankName} onChange={v => set('bankName', v)} /></FRow>
        <FRow label="Account Holder Name"><FInput value={data.accountHolderName} onChange={v => set('accountHolderName', v)} /></FRow>
        <FRow label="Account Number"><FInput value={data.accountNumber} onChange={v => set('accountNumber', v)} /></FRow>
        <FRow label="IFSC Code"><FInput value={data.ifscCode} onChange={v => set('ifscCode', v)} /></FRow>
        <FRow label="Branch"><FInput value={data.branch} onChange={v => set('branch', v)} /></FRow>
      </div>

      <SectionHead label="F. Nominee Details (for Dependent Benefit in case of Death)" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FRow label="Nominee Name"><FInput value={data.nomineeName} onChange={v => set('nomineeName', v)} /></FRow>
        <FRow label="Relationship"><FInput value={data.nomineeRelationship} onChange={v => set('nomineeRelationship', v)} /></FRow>
        <FRow label="Date of Birth"><FInput type="date" value={data.nomineeDateOfBirth} onChange={v => set('nomineeDateOfBirth', v)} /></FRow>
        <FRow label="Aadhaar Number"><FInput value={data.nomineeAadhaar} onChange={v => set('nomineeAadhaar', v)} /></FRow>
        <FRow label="Contact Number"><FInput value={data.nomineeContact} onChange={v => set('nomineeContact', v)} /></FRow>
      </div>

      <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--canvas-warm)', borderRadius: 8, fontSize: 13, color: 'var(--ink-muted)', fontStyle: 'italic' }}>
        Declaration: I hereby declare that the above information is true and correct. I understand that incorrect information may result in rejection or delay of ESIC registration and benefits.
      </div>
    </ModalShell>
  );
}

// ── Gratuity Form F ───────────────────────────────────────────
function GratuityFormModal({ apiKey, applicant, job, onClose, toast }) {
  const kf = loadKF();
  // Map ESIC maritalStatus → Gratuity options where they align
  const gratMarital = { 'Married': 'Married', 'Single': 'Unmarried' }[kf.maritalStatus] || '';
  const [data, setData] = useState({
    nominees: [{ name: '', address: '', relationship: '', age: '', proportion: '' }],
    sex:           kf.gender      || '',
    religion:      kf.religion    || '',
    maritalStatus: gratMarital,
    departmentBranch: job?.department || '',
    postHeld: '', dateOfAppointment: '',
    permanentAddress: { village: '', thana: '', subDivision: '', postOffice: '', district: '', state: '' },
    certifiedNoFamily: false,
    fatherMotherNotDependent: false,
    husbandParentsNotDependent: false,
    excludedHusband: false,
    witnesses: [{ name: '', address: '' }, { name: '', address: '' }],
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(`${API}/onboarding/employee/${apiKey}/statutory/gratuityForm`)
      .then(r => r.json()).then(d => {
        if (d && Object.keys(d).length > 0) setData(prev => ({ ...prev, ...d }));
        setLoaded(true);
      }).catch(() => setLoaded(true));
  }, [apiKey]);

  const KF_GRAT = { sex: 'gender', religion: 'religion', maritalStatus: 'maritalStatus' };
  const set = (key, val) => {
    if (KF_GRAT[key]) saveKF({ [KF_GRAT[key]]: val });
    setData(p => ({ ...p, [key]: val }));
  };
  const setNominee = (i, field, val) => setData(p => {
    const arr = [...(p.nominees || [])];
    arr[i] = { ...arr[i], [field]: val };
    return { ...p, nominees: arr };
  });
  const setWitness = (i, field, val) => setData(p => {
    const arr = [...(p.witnesses || [{}, {}])];
    arr[i] = { ...arr[i], [field]: val };
    return { ...p, witnesses: arr };
  });
  const setAddr = (field, val) => setData(p => ({ ...p, permanentAddress: { ...(p.permanentAddress || {}), [field]: val } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/onboarding/employee/${apiKey}/statutory/gratuityForm`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, signedAt: new Date().toISOString() })
      });
      if (!res.ok) throw new Error((await res.json()).error);
      toast.success('Gratuity Form F saved ');
      onClose();
    } catch (err) { toast.error(err.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (!loaded) return null;

  return (
    <ModalShell title="Gratuity Form F, Nomination"
      subtitle="Payment of Gratuity (Central) Rules, See sub-rule (1) of Rule 6"
      onClose={onClose} onSave={handleSave} saving={saving}>

      <div style={{ fontSize: 13, color: 'var(--ink-muted)', marginBottom: 16, lineHeight: 1.6 }}>
        I, <strong>{applicant?.name || '___________'}</strong>, hereby nominate the person(s) below to receive the gratuity payable after my death, and direct that the gratuity shall be paid in the proportion indicated against the nominee(s).
      </div>

      <SectionHead label="Nominees" />
      {(data.nominees || []).map((n, i) => (
        <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px', marginBottom: 12, background: 'var(--field)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <strong style={{ fontSize: 13 }}>Nominee {i + 1}</strong>
            {i > 0 && <button onClick={() => setData(p => ({ ...p, nominees: p.nominees.filter((_, j) => j !== i) }))}
              style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12 }}>Remove</button>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FRow label="Full Name"><FInput value={n.name} onChange={v => setNominee(i, 'name', v)} /></FRow>
            <FRow label="Relationship"><FInput value={n.relationship} onChange={v => setNominee(i, 'relationship', v)} placeholder="e.g. Spouse, Child" /></FRow>
            <FRow label="Age"><FInput value={n.age} onChange={v => setNominee(i, 'age', v)} /></FRow>
            <FRow label="Proportion (%)"><FInput value={n.proportion} onChange={v => setNominee(i, 'proportion', v)} placeholder="e.g. 100%" /></FRow>
          </div>
          <FRow label="Full Address"><textarea className="form-input form-textarea" rows={2} value={n.address || ''} onChange={e => setNominee(i, 'address', e.target.value)} /></FRow>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => setData(p => ({ ...p, nominees: [...(p.nominees || []), { name: '', address: '', relationship: '', age: '', proportion: '' }] }))}>
        + Add Nominee
      </Button>

      <SectionHead label="Statement (Employee Details)" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FRow label="Sex"><FSelect value={data.sex} onChange={v => set('sex', v)} options={['Male', 'Female', 'Other']} /></FRow>
        <FRow label="Religion"><FInput value={data.religion} onChange={v => set('religion', v)} /></FRow>
        <FRow label="Marital Status"><FSelect value={data.maritalStatus} onChange={v => set('maritalStatus', v)} options={['Unmarried', 'Married', 'Widow', 'Widower']} /></FRow>
        <FRow label="Department / Branch / Section"><FInput value={data.departmentBranch} onChange={v => set('departmentBranch', v)} /></FRow>
        <FRow label="Post Held"><FInput value={data.postHeld} onChange={v => set('postHeld', v)} /></FRow>
        <FRow label="Date of Appointment"><FInput type="date" value={data.dateOfAppointment} onChange={v => set('dateOfAppointment', v)} /></FRow>
      </div>

      <SectionHead label="Permanent Address" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        {[['village','Village'], ['thana','Thana'], ['subDivision','Sub-division'], ['postOffice','Post Office'], ['district','District'], ['state','State']].map(([f, l]) => (
          <FRow key={f} label={l}><FInput value={data.permanentAddress?.[f]} onChange={v => setAddr(f, v)} /></FRow>
        ))}
      </div>

      <SectionHead label="Declarations" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <FCheck label="Certified that the person(s) mentioned is/are a member(s) of my family within the meaning of clause (h) of Section 2 of the Payment of Gratuity Act, 1972" checked={true} onChange={() => {}} />
        <FCheck label="I hereby declare that I have no family within the meaning of clause (h) of Section 2 of the said Act" checked={data.certifiedNoFamily} onChange={v => set('certifiedNoFamily', v)} />
        <FCheck label="My father/mother/parents is/are not dependent on me" checked={data.fatherMotherNotDependent} onChange={v => set('fatherMotherNotDependent', v)} />
        <FCheck label="My husband's father/mother/parents is/are not dependent on my husband" checked={data.husbandParentsNotDependent} onChange={v => set('husbandParentsNotDependent', v)} />
        <FCheck label="I have excluded my husband from my family by a notice to the controlling authority" checked={data.excludedHusband} onChange={v => set('excludedHusband', v)} />
      </div>

      <SectionHead label="Witnesses" />
      {[0, 1].map(i => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
          <FRow label={`Witness ${i + 1}, Full Name & Address`}>
            <FInput value={data.witnesses?.[i]?.name} onChange={v => setWitness(i, 'name', v)} placeholder="Name" />
          </FRow>
          <FRow label=" ">
            <FInput value={data.witnesses?.[i]?.address} onChange={v => setWitness(i, 'address', v)} placeholder="Address" />
          </FRow>
        </div>
      ))}
    </ModalShell>
  );
}

// ── Field renderer ────────────────────────────────────────────
function FieldRenderer({ field, value, fileUpload, uploading, onChange, onFileUpload }) {
  const inputRef = useRef();

  if (['image', 'document', 'file'].includes(field.type)) {
    return (
      <div className="form-group">
        <label className="form-label">
          {field.label} {field.required && <span style={{ color: 'var(--accent)' }}>*</span>}
        </label>
        <div onClick={() => inputRef.current?.click()} style={{
          border: '2px dashed', borderColor: fileUpload ? 'var(--sage)' : 'var(--border-strong)',
          borderRadius: 'var(--radius-md)', padding: '20px', textAlign: 'center',
          cursor: 'pointer', background: fileUpload ? 'var(--sage-pale)' : 'var(--canvas-warm)', transition: 'all 0.2s'
        }}>
          <input ref={inputRef} type="file" accept={field.accept || '*'} style={{ display: 'none' }}
            onChange={e => e.target.files[0] && onFileUpload(e.target.files[0])} />
          {uploading ? (
            <div><div className="spinner" style={{ margin: '0 auto', width: 20, height: 20 }} /><div style={{ fontSize: 13, marginTop: 8 }}>Uploading…</div></div>
          ) : fileUpload ? (
            <div style={{ color: 'var(--sage)' }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{fileUpload.fileName}</div>
              <div style={{ fontSize: 11, color: 'var(--ink-muted)', marginTop: 4 }}>Click to replace</div>
            </div>
          ) : (
            <div style={{ color: 'var(--ink-muted)' }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Click to upload {field.type === 'image' ? 'photo' : 'document'}</div>
              {field.accept && <div style={{ fontSize: 11, marginTop: 4 }}>{field.accept} · max {field.maxSizeMB || 5}MB</div>}
            </div>
          )}
        </div>
        {field.helpText && <span className="form-hint">{field.helpText}</span>}
      </div>
    );
  }

  return (
    <div className="form-group">
      <label className="form-label">
        {field.label} {field.required && <span style={{ color: 'var(--accent)' }}>*</span>}
      </label>
      {field.type === 'textarea' && (
        <textarea className="form-input form-textarea" rows={3} placeholder={field.placeholder}
          value={value} onChange={e => onChange(e.target.value)} />
      )}
      {field.type === 'select' && (
        <select className="form-input form-select" value={value} onChange={e => onChange(e.target.value)}>
          <option value="">Select…</option>
          {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      )}
      {field.type === 'checkbox' && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />
          {field.placeholder || field.label}
        </label>
      )}
      {['text', 'phone', 'email', 'number', 'date'].includes(field.type) && (
        <input className="form-input"
          type={field.type === 'phone' ? 'tel' : field.type}
          placeholder={field.placeholder} value={value}
          onChange={e => onChange(e.target.value)} />
      )}
      {field.helpText && <span className="form-hint">{field.helpText}</span>}
    </div>
  );
}

// ── Document acknowledge card with timer ──────────────────────
function DocAcknowledgeCard({ doc, acknowledged, apiKey, onAcknowledged, toast }) {
  const timerKey = `soch_doc_timer_${doc.id}`;

  // readTimeMinutes comes live from the doc object, HR changes apply immediately
  const requiredSeconds = (doc.readTimeMinutes || 4) * 60;

  // Restore saved time across refresh/back
  const [timeSpent, setTimeSpent] = useState(() => {
    if (acknowledged) return requiredSeconds;
    try {
      const saved = JSON.parse(localStorage.getItem(timerKey) || 'null');
      if (!saved) return 0;
      if (saved.openedAt) return saved.accumulated + Math.floor((Date.now() - saved.openedAt) / 1000);
      return saved.accumulated;
    } catch { return 0; }
  });
  const [open, setOpen] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const timerRef = useRef(null);

  const canAcknowledge = timeSpent >= requiredSeconds;
  const progress  = Math.min((timeSpent / requiredSeconds) * 100, 100);
  const remaining = Math.max(requiredSeconds - timeSpent, 0);

  const openDoc = () => {
    setOpen(true);
    localStorage.setItem(timerKey, JSON.stringify({ accumulated: timeSpent, openedAt: Date.now() }));
    timerRef.current = setInterval(() => {
      setTimeSpent(prev => {
        const next = prev + 1;
        localStorage.setItem(timerKey, JSON.stringify({ accumulated: next, openedAt: Date.now() - (next * 1000 - timeSpent * 1000) }));
        return next;
      });
    }, 1000);
  };

  const closeDoc = () => {
    setOpen(false);
    clearInterval(timerRef.current);
    localStorage.setItem(timerKey, JSON.stringify({ accumulated: timeSpent, openedAt: null }));
  };

  useEffect(() => () => { clearInterval(timerRef.current); }, []);

  const acknowledge = async () => {
    setAcknowledging(true);
    try {
      const res = await fetch(`${API}/onboarding/employee/${apiKey}/acknowledge`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ docId: doc.id, timeSpentSeconds: timeSpent })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      onAcknowledged(doc.id);
      closeDoc();
      toast.success(`"${doc.title}" acknowledged `);
    } catch (err) { toast.error(err.message); }
    finally { setAcknowledging(false); }
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <>
      <div style={{
        background: acknowledged ? 'var(--sage-pale)' : 'var(--canvas-warm)',
        border: '1px solid', borderColor: acknowledged ? 'var(--sage)' : 'var(--border)',
        borderRadius: 'var(--radius-md)', padding: '16px 20px', marginBottom: 12,
        display: 'flex', alignItems: 'center', gap: 16
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{doc.title}</div>
          {doc.description && <div style={{ fontSize: 13, color: 'var(--ink-muted)', marginTop: 2 }}>{doc.description}</div>}
          <div style={{ fontSize: 12, color: 'var(--ink-muted)', marginTop: 4 }}>
            Min. read time: {doc.readTimeMinutes} min{doc.readTimeMinutes !== 1 ? 's' : ''}
            {timeSpent > 0 && !acknowledged && <span style={{ marginLeft: 8, color: timeSpent >= requiredSeconds ? 'var(--sage)' : 'var(--gold)' }}>
              · Time spent: {fmt(timeSpent)}
            </span>}
          </div>
          {!acknowledged && timeSpent > 0 && (
            <div style={{ marginTop: 8 }}>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%`, background: canAcknowledge ? 'var(--sage)' : 'var(--gold)' }} />
              </div>
              {!canAcknowledge && <div style={{ fontSize: 11, color: 'var(--ink-muted)', marginTop: 4 }}>{fmt(remaining)} remaining before you can acknowledge</div>}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {acknowledged ? (
            <span style={{ fontSize: 13, color: 'var(--sage)', fontWeight: 600 }}>Acknowledged </span>
          ) : (
            <>
              {doc.fileUrl && (
                <Button variant="outline" size="sm" onClick={openDoc}>
                  Read Document
                </Button>
              )}
              {canAcknowledge && (
                <Button variant="success" size="sm" onClick={acknowledge} loading={acknowledging}>
                  I Acknowledge
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Doc viewer modal */}
      {open && doc.fileUrl && typeof document !== 'undefined' && (() => {
        const ReactDOM = require('react-dom');
        return ReactDOM.createPortal(
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', flexDirection: 'column' }}>
            {/* Toolbar */}
            <div style={{ background: 'var(--ink)', color: 'white', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <div style={{ fontWeight: 600 }}>{doc.title}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>
                  {canAcknowledge
                    ? 'You can now acknowledge this document'
                    : `Please read for at least ${fmt(remaining)} more before acknowledging`}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {/* Timer ring */}
                <div style={{ position: 'relative', width: 48, height: 48 }}>
                  <svg width="48" height="48" viewBox="0 0 48 48">
                    <circle cx="24" cy="24" r="20" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="4" />
                    <circle cx="24" cy="24" r="20" fill="none"
                      stroke={canAcknowledge ? '#4a7c59' : '#b8963e'} strokeWidth="4"
                      strokeDasharray={`${progress * 1.257} 125.7`}
                      strokeLinecap="round" transform="rotate(-90 24 24)" />
                  </svg>
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'white', fontWeight: 700 }}>
                    {canAcknowledge ? 'OK' : fmt(remaining)}
                  </div>
                </div>
                {canAcknowledge && (
                  <Button variant="success" size="sm" onClick={acknowledge} loading={acknowledging}>
                    Acknowledge
                  </Button>
                )}
                <button onClick={closeDoc} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.3)', color: 'white', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Close
                </button>
              </div>
            </div>
            {/* PDF embed */}
            <iframe
              src={`${fileHref(doc.fileUrl, apiKey)}`}
              style={{ flex: 1, border: 'none', background: 'var(--field)' }}
              title={doc.title}
            />
          </div>,
          document.body
        );
      })()}
    </>
  );
}

// ── Employee Task Card ────────────────────────────────────────
// Timer key, persists across refreshes/back button
function getTimerKey(taskId) { return `soch_timer_${taskId}`; }
function getSavedTime(taskId) {
  try {
    const saved = JSON.parse(localStorage.getItem(getTimerKey(taskId)) || 'null');
    if (!saved) return 0;
    // If document was open, add elapsed time since last save
    if (saved.openedAt) {
      const elapsed = Math.floor((Date.now() - saved.openedAt) / 1000);
      return saved.accumulated + elapsed;
    }
    return saved.accumulated;
  } catch { return 0; }
}
function saveTimer(taskId, accumulated, isOpen) {
  localStorage.setItem(getTimerKey(taskId), JSON.stringify({
    accumulated,
    openedAt: isOpen ? Date.now() : null
  }));
}

function EmployeeTaskCard({ task, apiKey, onUpdated, toast }) {
  const a = task.assignmentId || {};

  // Always use LIVE readTimeMinutes from the assignment (HR may have changed it)
  const requiredSec = (a.readTimeMinutes || 0) * 60;

  // Restore accumulated time from localStorage (persists across refresh/back)
  const [timeSpent, setTimeSpent] = React.useState(() => {
    // If already acknowledged, use server value
    if (task.status === 'acknowledged') return task.timeSpentSeconds || 0;
    // If reopened, reset timer
    if (task.status === 'reopened') {
      localStorage.removeItem(getTimerKey(task._id));
      return 0;
    }
    return getSavedTime(task._id);
  });

  const [docOpen, setDocOpen] = React.useState(false);
  const [acking,  setAcking]  = React.useState(false);
  const [file,    setFile]    = React.useState(null);
  const timerRef  = React.useRef(null);
  const fileRef   = React.useRef();

  // When task is reopened by HR, clear stored timer
  React.useEffect(() => {
    if (task.status === 'reopened') {
      localStorage.removeItem(getTimerKey(task._id));
      setTimeSpent(0);
    }
  }, [task.status, task._id]);

  const canAck    = (!requiredSec || timeSpent >= requiredSec) && (!a.requiresFile || file || task.uploadedFileUrl);
  const progress  = requiredSec > 0 ? Math.min((timeSpent / requiredSec) * 100, 100) : 100;
  const remaining = Math.max(requiredSec - timeSpent, 0);
  const fmt       = s => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;

  const isDone     = task.status === 'acknowledged';
  const isReopened = task.status === 'reopened';

  const openDoc = async () => {
    setDocOpen(true);
    // Mark open time in localStorage so back button doesn't lose it
    saveTimer(task._id, timeSpent, true);
    timerRef.current = setInterval(() => {
      setTimeSpent(prev => {
        const next = prev + 1;
        saveTimer(task._id, next, true);
        return next;
      });
    }, 1000);
    if (task.status === 'pending' || task.status === 'reopened') {
      await fetch(`${API}/assignments/my-tasks/${apiKey}/${task._id}/open`, { method: 'POST' });
    }
  };

  const closeDoc = () => {
    setDocOpen(false);
    clearInterval(timerRef.current);
    // Save accumulated time, mark as not currently open
    saveTimer(task._id, timeSpent, false);
  };

  // Cleanup on unmount, save timer state
  React.useEffect(() => () => {
    clearInterval(timerRef.current);
    if (docOpen) saveTimer(task._id, timeSpent, false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const acknowledge = async () => {
    setAcking(true);
    try {
      const fd = new FormData();
      fd.append('timeSpentSeconds', timeSpent);
      if (file) fd.append('file', file);
      const res = await fetch(`${API}/assignments/my-tasks/${apiKey}/${task._id}/acknowledge`, { method: 'POST', body: fd });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      toast.success('Task acknowledged ');
      onUpdated();
    } catch (err) { toast.error(err.message); }
    finally { setAcking(false); }
  };

  const ReactDOM = require('react-dom');

  return (
    <>
      <div style={{
        background: isDone ? 'var(--sage-pale)' : isReopened ? 'var(--accent-pale)' : 'var(--canvas-warm)',
        border: '1px solid', borderColor: isDone ? 'var(--sage)' : isReopened ? 'var(--accent)' : 'var(--border)',
        borderRadius: 'var(--radius-md)', padding: '16px 20px', marginBottom: 12
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
              <strong style={{ fontSize: 15 }}>{a.title}</strong>
              {isDone && <span style={{ fontSize: 11, color: 'var(--sage)', fontWeight: 700 }}>ACKNOWLEDGED</span>}
              {isReopened && <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 700 }}>RE-REVIEW REQUIRED</span>}
              {a.readTimeMinutes > 0 && !isDone && <span style={{ fontSize: 11, color: 'var(--gold)' }}>{a.readTimeMinutes} min read</span>}
              {a.requiresFile && !isDone && <span style={{ fontSize: 11, color: 'var(--accent)' }}>Upload required</span>}
            </div>
            {a.description && <p style={{ fontSize: 13, color: 'var(--ink-muted)', margin: '0 0 10px' }}>{a.description}</p>}
            {isReopened && task.reopenReason && (
              <div style={{ fontSize: 12, color: 'var(--accent)', marginBottom: 10, fontStyle: 'italic' }}>
                HR note: {task.reopenReason}
              </div>
            )}

            {/* Progress bar, shows whenever there is read time requirement */}
            {!isDone && requiredSec > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--ink-muted)', marginBottom: 4 }}>
                  <span>{canAck ? 'Ready to acknowledge' : timeSpent > 0 ? `Read ${fmt(timeSpent)} of ${fmt(requiredSec)}` : `Must read for ${fmt(requiredSec)}`}</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${progress}%`, background: canAck ? 'var(--sage)' : timeSpent > 0 ? 'var(--gold)' : 'var(--border-strong)', transition: 'width 1s linear' }} />
                </div>
                {!canAck && timeSpent > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--ink-muted)', marginTop: 3 }}>
                    {fmt(remaining)} remaining, keep the document open
                  </div>
                )}
              </div>
            )}

            {/* File upload if required */}
            {a.requiresFile && !isDone && (
              <div style={{ marginBottom: 10 }}>
                <div onClick={() => fileRef.current?.click()} style={{
                  border: '1.5px dashed var(--border-strong)', borderRadius: 'var(--radius-sm)',
                  padding: '10px 14px', cursor: 'pointer', background: (file || task.uploadedFileUrl) ? 'var(--sage-pale)' : 'white',
                  display: 'flex', alignItems: 'center', gap: 8, fontSize: 13
                }}>
                  <input ref={fileRef} type="file" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
                  
                  <span style={{ color: (file || task.uploadedFileUrl) ? 'var(--sage)' : 'var(--ink-muted)' }}>
                    {file ? file.name : task.uploadedFileName ? `Uploaded: ${task.uploadedFileName}` : (a.filePrompt || 'Click to upload required file')}
                  </span>
                </div>
              </div>
            )}

            {/* Actions */}
            {!isDone && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {a.fileUrl && (
                  <Button variant="outline" size="sm" onClick={openDoc}>
                    {timeSpent > 0 ? `Continue Reading (${fmt(timeSpent)})` : 'Open & Read Document'}
                  </Button>
                )}
                {canAck && (
                  <Button variant="success" size="sm" onClick={acknowledge} loading={acking}>
                    I Acknowledge
                  </Button>
                )}
                {!a.fileUrl && !a.requiresFile && !canAck && (
                  <Button variant="primary" size="sm" onClick={acknowledge} loading={acking}>
                    Mark Complete
                  </Button>
                )}
              </div>
            )}
            {isDone && (
              <div style={{ fontSize: 12, color: 'var(--sage)' }}>
                Acknowledged on {new Date(task.acknowledgedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                {task.timeSpentSeconds > 60 && ` · ${Math.floor(task.timeSpentSeconds/60)} min read`}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PDF Viewer modal */}
      {docOpen && a.fileUrl && ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', flexDirection: 'column' }}>
          <div style={{ background: 'var(--ink)', color: 'white', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <div>
              <div style={{ fontWeight: 600 }}>{a.title}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>
                {canAck ? 'Ready to acknowledge' : `${fmt(remaining)} remaining`}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {requiredSec > 0 && (
                <div style={{ position: 'relative', width: 44, height: 44 }}>
                  <svg width="44" height="44" viewBox="0 0 44 44">
                    <circle cx="22" cy="22" r="18" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="4" />
                    <circle cx="22" cy="22" r="18" fill="none" stroke={canAck ? '#4a7c59' : '#b8963e'} strokeWidth="4"
                      strokeDasharray={`${progress * 1.131} 113.1`} strokeLinecap="round" transform="rotate(-90 22 22)" />
                  </svg>
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'white', fontWeight: 700 }}>
                    {canAck ? 'OK' : fmt(remaining)}
                  </div>
                </div>
              )}
              {canAck && <Button variant="success" size="sm" onClick={() => { closeDoc(); acknowledge(); }} loading={acking}>Acknowledge</Button>}
              <button onClick={closeDoc} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.3)', color: 'white', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Close</button>
            </div>
          </div>
          <iframe src={`${fileHref(a.fileUrl, apiKey)}`}
            style={{ flex: 1, border: 'none', background: 'var(--field)' }} title={a.title} />
        </div>,
        document.body
      )}
    </>
  );
}
